import React, { useState } from 'react';
import { PropTypes } from 'prop-types';
import Button from '../Button/Button';
import CommonModal from '../Common/Modal/Modal';
import NewEditContactModal from './NewEditContactModal';
import Icon from '../Icons/Icon';
import { useUnsavedChangesWarning, useCloseDialogWithUnsavedChanges } from '../../hooks';
import { useUrlModals } from '../../utils/useUrlModals';
import { convertUrlsToLinks } from '../../utils/urlFinder';

const DEFAULT_INITIAL_MODEL_FORM_ITEMS = [];

const ContactInfo = ({
  organizationId, contact: initialContact, isManager, isPlanner, onDelete = () => { }, initialModelFormItems = DEFAULT_INITIAL_MODEL_FORM_ITEMS, onContactUpdate = () => { }, showToast, setCurrentContact,
}) => {
  const canEdit = isManager || isPlanner;
  // eslint-disable-next-line no-unused-vars
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [contact, setContact] = useState(initialContact);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const { modals, openModal, closeModal } = useUrlModals({
    edit: { isIdParam: false },
  });

  useUnsavedChangesWarning(hasUnsavedChanges);

  const handleCloseDialogWithUnsavedChanges = useCloseDialogWithUnsavedChanges({
    isUnsavedChanges: hasUnsavedChanges,
    setIsUnsavedChanges: setHasUnsavedChanges,
    redirectCallback: () => closeModal('edit'),
  });

  const contactFields = [
    { label: 'Name', value: contact.name },
    { label: 'Phone', value: contact.phone },
    { label: 'Company', value: contact.company },
    { label: 'Contact type', value: contact.contact_type },
    { label: 'Address line 1', value: contact.address1 },
    { label: 'Address line 2', value: contact.address2 },
    { label: 'City', value: contact.city },
    { label: 'State', value: contact.state },
    { label: 'ZIP code', value: contact.zip_code },
    { label: 'Email', value: contact.email },
    { label: 'Note', value: contact.note },
  ];

  const half = Math.ceil(contactFields.length / 2);
  const leftFields = contactFields.slice(0, half);
  const rightFields = contactFields.slice(half);
  const fieldGroups = [
    { id: 'left', fields: leftFields },
    { id: 'right', fields: rightFields },
  ];

  return (
    <div>
      <header className="flex justify-between items-center border-b border-vamos-blue-gray pb-3 mb-6">
        <div className="flex items-center gap-2.5">
          <Icon name="contact" />
          <h2 className="font-bold text-2xl text-vamos-black font-sans">{contact.name}</h2>
        </div>
        <div className="flex space-x-4">
          {isManager && (
            <Button
              label="Delete"
              size="md"
              onClick={onDelete}
              color="secondary"
              className="w-21"
            />
          )}
          {canEdit && (
            <Button
              label="Edit"
              size="md"
              onClick={() => openModal('edit')}
              className="w-21"
            />
          )}

        </div>
      </header>

      <main className="grid grid-cols-1 md:grid-cols-2 gap-x-45 mb-16.5">
        {fieldGroups.map(({ id, fields }) => (
          <div key={id} className="space-y-3">
            {fields.map(({ label, value }) => (
              <div key={label} className="grid grid-cols-2 gap-18.25">
                <h3 className="font-bold whitespace-nowrap">{label}</h3>
                <span className="break-words" data-testid={`contact-${label.toLocaleLowerCase()}`}>{convertUrlsToLinks(value) || ''}</span>
              </div>
            ))}
          </div>
        ))}
      </main>

      {/* Edit Contact Modal */}
      {modals.edit?.isOpen && (
        <CommonModal
          title="Edit Contact"
          titlePosition="left"
          className="py-0"
          headerClassName="h-18.5 pl-5.5"
          closeIconPosition="absolute right-5.5 top-6"
          titleStyle="text-2xl font-bold"
          boxClassName="w-1/2"
          handleClose={handleCloseDialogWithUnsavedChanges}
          isHeaderDivider
        >
          <NewEditContactModal
            contact={contact}
            organizationId={organizationId}
            initialModelFormItems={initialModelFormItems}
            onCancel={handleCloseDialogWithUnsavedChanges}
            showToast={showToast}
            setIsModalVisible={(visible) => (visible ? openModal('edit') : closeModal('edit'))}
            onContactUpdate={(updatedContact) => {
              setContact(updatedContact);
              onContactUpdate?.(updatedContact);
            }}
            setCurrentContact={setCurrentContact}
            setIsUnsavedChanges={setHasUnsavedChanges}
            closeModal={() => closeModal('edit')}
          />
        </CommonModal>
      )}
    </div>
  );
};

ContactInfo.propTypes = {
  contact: PropTypes.object.isRequired,
  isManager: PropTypes.bool.isRequired,
  isPlanner: PropTypes.bool.isRequired,
  onDelete: PropTypes.func,
  organizationId: PropTypes.number.isRequired,
  initialModelFormItems: PropTypes.array,
  onContactUpdate: PropTypes.func,
  showToast: PropTypes.func.isRequired,
  setCurrentContact: PropTypes.func.isRequired,
};

export default ContactInfo;
